import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import ImageUploader from "@/components/ImageUploader";
import {
  Sparkles, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff,
  ArrowUp, ArrowDown, Archive, RotateCcw,
} from "lucide-react";

const ICON_OPTIONS = ["shield-check", "gavel", "activity", "graduation-cap", "sparkles", "building", "scale"];
const EMPTY = {
  key: "", title_ar: "", title_en: "", desc_ar: "", desc_en: "",
  icon: "shield-check", image_url: "",
  features_ar: [], features_en: [],
  display_order: 0, is_active: true, is_archived: false,
  external_url: "", pdf_url: "",
  button_text_ar: "", button_text_en: "", show_button: false,
  regulatory_frameworks: "",
};

export default function AdminServices() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/admin/services").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const visible = useMemo(
    () => rows.filter((r) => (showArchived ? true : !r.is_archived)),
    [rows, showArchived],
  );

  const openNew = () =>
    setEditing({ ...EMPTY, display_order: (rows.reduce((m, r) => Math.max(m, r.display_order), -1) + 1) });
  const openEdit = (s) =>
    setEditing({
      ...EMPTY, ...s,
      features_ar: Array.isArray(s.features_ar) ? s.features_ar : [],
      features_en: Array.isArray(s.features_en) ? s.features_en : [],
    });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.key || !editing.title_en || !editing.title_ar) {
      toast.error("Key + titles are required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/services/${editing.id}`, editing);
      else await api.post("/admin/services", editing);
      toast.success("Saved"); close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleVisible = async (s) => {
    try { await api.put(`/admin/services/${s.id}/toggle`, { is_active: !s.is_active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (s, dir) => {
    const list = [...visible].sort((a, b) => a.display_order - b.display_order);
    const idx = list.findIndex((x) => x.id === s.id);
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    try {
      await api.put("/admin/services/reorder", { ordered_ids: list.map((x) => x.id) });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async (s) => {
    if (!window.confirm(`Archive "${s.title_en}"? It will disappear from the public site.`)) return;
    try { await api.post(`/admin/services/${s.id}/archive`); toast.success("Archived"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (s) => {
    try { await api.post(`/admin/services/${s.id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!s.is_archived) {
      toast.error("Move to Trash first before permanent delete.");
      return;
    }
    const label = s.title_en || s.key;
    if (!window.confirm(
      `PERMANENTLY delete "${label}"?\n\n` +
      `This cannot be undone. Existing submissions or quote requests that reference this service ` +
      `will keep their historical link but the service page will disappear from the site.\n\n` +
      `Type OK in the next prompt to confirm.`
    )) return;
    const typed = window.prompt('Type "DELETE" to confirm permanent deletion:');
    if (typed !== "DELETE") { toast.error("Deletion cancelled"); return; }
    try { await api.delete(`/admin/services/${s.id}`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Content</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <Sparkles size={26} className="text-gold" /> Services Management
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="svc-show-archived" />
            Show archived
          </label>
          <button onClick={openNew} className="btn-gold" data-testid="svc-add"><Plus size={14} /> New service</button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream"><tr>
            <th className="text-start p-3">Order</th>
            <th className="text-start p-3">Key</th>
            <th className="text-start p-3">Title (EN)</th>
            <th className="text-start p-3">Title (AR)</th>
            <th className="text-start p-3">Status</th>
            <th className="text-end p-3">Actions</th>
          </tr></thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-navy/50">No services yet — click New service.</td></tr>
            ) : visible
              .sort((a, b) => a.display_order - b.display_order)
              .map((s) => (
              <tr key={s.id} data-testid={`svc-row-${s.id}`} className="border-t border-navy/10 hover:bg-cream">
                <td className="p-3 text-navy/70 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <span>{s.display_order}</span>
                    <button onClick={() => move(s, -1)} className="text-navy/50 hover:text-gold p-1" title="Move up" data-testid={`svc-up-${s.id}`}><ArrowUp size={12} /></button>
                    <button onClick={() => move(s, +1)} className="text-navy/50 hover:text-gold p-1" title="Move down" data-testid={`svc-down-${s.id}`}><ArrowDown size={12} /></button>
                  </div>
                </td>
                <td className="p-3 text-navy/60 font-mono text-xs">{s.key}</td>
                <td className="p-3 text-navy font-medium">{s.title_en}</td>
                <td className="p-3 text-navy/80">{s.title_ar}</td>
                <td className="p-3">
                  {s.is_archived ? (
                    <Badge className="bg-navy/20 text-navy/70"><Archive size={11} className="inline me-1" />Archived</Badge>
                  ) : s.is_active ? (
                    <Badge className="bg-emerald-100 text-emerald-800"><Eye size={11} className="inline me-1" />Live</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800"><EyeOff size={11} className="inline me-1" />Hidden</Badge>
                  )}
                </td>
                <td className="p-3 text-end whitespace-nowrap">
                  {!s.is_archived && (
                    <button onClick={() => toggleVisible(s)} className="text-navy/70 hover:text-gold p-1.5" title={s.is_active ? "Hide" : "Show"} data-testid={`svc-toggle-${s.id}`}>
                      {s.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  )}
                  <button onClick={() => openEdit(s)} className="text-navy/70 hover:text-gold p-1.5" title="Edit" data-testid={`svc-edit-${s.id}`}><Edit2 size={14} /></button>
                  {s.is_archived ? (
                    <button onClick={() => restore(s)} className="text-emerald-700 hover:text-emerald-800 p-1.5" title="Restore" data-testid={`svc-restore-${s.id}`}><RotateCcw size={14} /></button>
                  ) : (
                    <button onClick={() => archive(s)} className="text-amber-700 hover:text-amber-800 p-1.5" title="Archive" data-testid={`svc-archive-${s.id}`}><Archive size={14} /></button>
                  )}
                  <button onClick={() => remove(s)} className="text-red-600/80 hover:text-red-700 p-1.5" title="Delete" data-testid={`svc-del-${s.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white max-w-3xl w-full rounded-md border border-gold/30 p-8 max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()} data-testid="svc-editor">
            <div className="flex justify-between items-start">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit service" : "New service"}</h2>
              <button onClick={close} className="text-navy/50 hover:text-navy"><X size={18} /></button>
            </div>
            <span className="divider-gold" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className="field-box" placeholder="Key (e.g. governance)" value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value.trim().toLowerCase() })} data-testid="svc-key" />
              <select className="field-box" value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} data-testid="svc-icon">
                {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <input className="field-box" placeholder="Title (AR)" value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} data-testid="svc-title-ar" />
              <input className="field-box" placeholder="Title (EN)" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="svc-title-en" />
              <textarea className="field-box sm:col-span-2 min-h-[80px]" placeholder="Description (AR)" value={editing.desc_ar} onChange={(e) => setEditing({ ...editing, desc_ar: e.target.value })} data-testid="svc-desc-ar" />
              <textarea className="field-box sm:col-span-2 min-h-[80px]" placeholder="Description (EN)" value={editing.desc_en} onChange={(e) => setEditing({ ...editing, desc_en: e.target.value })} data-testid="svc-desc-en" />

              <div className="sm:col-span-2">
                <label className="text-xs text-navy/70 mb-2 block">Service image (optional)</label>
                <ImageUploader
                  value={editing.image_url || ""}
                  folder="services"
                  testId="svc-image"
                  onChange={(url) => setEditing({ ...editing, image_url: url || "" })}
                />
              </div>

              <div>
                <label className="text-xs text-navy/70 mb-1 block">Features (AR) — one per line</label>
                <textarea className="field-box min-h-[100px]" value={(editing.features_ar || []).join("\n")}
                          onChange={(e) => setEditing({ ...editing, features_ar: e.target.value.split("\n").map((v) => v).filter((v) => v.trim()) })}
                          data-testid="svc-features-ar" />
              </div>
              <div>
                <label className="text-xs text-navy/70 mb-1 block">Features (EN) — one per line</label>
                <textarea className="field-box min-h-[100px]" value={(editing.features_en || []).join("\n")}
                          onChange={(e) => setEditing({ ...editing, features_en: e.target.value.split("\n").map((v) => v).filter((v) => v.trim()) })}
                          data-testid="svc-features-en" />
              </div>

              <input className="field-box" type="number" placeholder="Display order" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) || 0 })} data-testid="svc-order" />
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="svc-active" />
                Show on public site
              </label>

              <div className="sm:col-span-2 border-t border-navy/10 pt-4 mt-2">
                <div className="text-xs uppercase tracking-widest text-navy/60 mb-3">Optional CTA (call-to-action)</div>
                <label className="flex items-center gap-2 text-navy mb-3">
                  <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.show_button} onChange={(e) => setEditing({ ...editing, show_button: e.target.checked })} data-testid="svc-show-button" />
                  Show CTA buttons on the public services page
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input className="field-box" placeholder="External link (https://…)" value={editing.external_url || ""} onChange={(e) => setEditing({ ...editing, external_url: e.target.value })} data-testid="svc-external-url" />
                  <input className="field-box" placeholder="PDF link (https://…)" value={editing.pdf_url || ""} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} data-testid="svc-pdf-url" />
                  <input className="field-box" placeholder="Button text (AR)" value={editing.button_text_ar || ""} onChange={(e) => setEditing({ ...editing, button_text_ar: e.target.value })} data-testid="svc-btn-ar" />
                  <input className="field-box" placeholder="Button text (EN)" value={editing.button_text_en || ""} onChange={(e) => setEditing({ ...editing, button_text_en: e.target.value })} data-testid="svc-btn-en" />
                </div>
              </div>

              <div className="sm:col-span-2 border-t border-navy/10 pt-4 mt-2">
                <label className="text-xs text-navy/70 mb-1 block">
                  Regulatory frameworks (CSV — e.g. <code>pdpl,gdpr</code>)
                </label>
                <input className="field-box w-full"
                       placeholder="pdpl,gdpr,uae_pdpl,qatar_pdp"
                       value={editing.regulatory_frameworks || ""}
                       onChange={(e) => setEditing({ ...editing, regulatory_frameworks: e.target.value })}
                       data-testid="svc-reg" />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-40" data-testid="svc-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={close} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
